import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, ArrowRight, ArrowLeft, CheckCircle2, Sparkle } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { PROGRAM_DETAILS } from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";

export default function ProgramDetail() {
  const { slug } = useParams();
  const { openBook } = useEnquiry();
  const idx = PROGRAM_DETAILS.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to="/programs" replace />;

  const program = PROGRAM_DETAILS[idx];
  const prev = PROGRAM_DETAILS[(idx + PROGRAM_DETAILS.length - 1) % PROGRAM_DETAILS.length];
  const next = PROGRAM_DETAILS[(idx + 1) % PROGRAM_DETAILS.length];

  return (
    <main data-testid="program-detail-page" className="bg-[#f7f2e8]">
      {/* Hero */}
      <section className="relative bg-[#070604] overflow-hidden pt-[72px] md:pt-20">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 py-14 md:py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <FadeIn>
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold tracking-[0.24em] uppercase text-[#c9a14a]">
              <Link to="/" className="text-[#f7f2e8]/60 hover:text-[#c9a14a] transition-colors">Home</Link>
              <ChevronRight size={11} className="text-[#f7f2e8]/40" />
              <Link to="/programs" className="text-[#f7f2e8]/60 hover:text-[#c9a14a] transition-colors">Wellness Programs</Link>
              <ChevronRight size={11} className="text-[#f7f2e8]/40" />
              <span>{program.title}</span>
            </div>
            <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.06] text-[#f7f2e8]" data-testid="program-title">
              {program.title}
            </h1>
            <p className="mt-3 font-serif-italic text-2xl text-[#c9a14a]">{program.tagline}</p>
            <p className="mt-6 text-[#f7f2e8]/80 text-base md:text-lg leading-relaxed max-w-xl">{program.desc}</p>
            <button onClick={() => openBook("assessment")} data-testid="program-book-assessment-button" className="btn-gold mt-9">
              Book Your Assessment <ArrowRight size={15} strokeWidth={2} />
            </button>
          </FadeIn>
          <FadeIn delay={0.15} className="rounded-2xl overflow-hidden shadow-2xl max-h-[480px]">
            <img src={program.image} alt={program.title} data-testid="program-hero-image" className="w-full h-full object-cover aspect-[4/3]" />
          </FadeIn>
        </div>
      </section>

      {/* Therapies */}
      <section data-testid="program-therapies" className="bg-[#f7f2e8]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn>
            <p className="overline text-[#c9a14a]">What's Included</p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-[#141414]">
              Signature <span className="text-[#c9a14a]">Therapies & Services</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {program.therapies.map((t, i) => (
              <FadeIn key={t} delay={i * 0.06} className="flex items-center gap-4 bg-white border border-[#c9a14a]/20 p-6 hover:border-[#c9a14a]/50 transition-colors duration-400">
                <span className="w-10 h-10 shrink-0 rounded-full bg-[#c9a14a]/12 border border-[#c9a14a]/40 flex items-center justify-center text-[#a8813a]">
                  <Sparkle size={16} strokeWidth={1.5} />
                </span>
                <p className="text-[14px] font-medium text-[#141414]">{t}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="program-benefits" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn>
            <p className="overline text-[#c9a14a]">Why It Works</p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-[#f7f2e8]">
              Key <span className="text-[#c9a14a]">Benefits</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7">
            {program.benefits.map((b, i) => (
              <FadeIn key={b} delay={i * 0.06} className="flex items-center gap-3">
                <CheckCircle2 size={18} className="text-[#c9a14a] shrink-0" strokeWidth={1.6} />
                <p className="text-[15px] text-[#f7f2e8]/90">{b}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + prev/next */}
      <section className="bg-[#f2ede3]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-16 md:py-20 text-center">
          <FadeIn>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#141414]">
              Begin your <span className="font-serif-italic text-[#c9a14a]">journey</span> today.
            </h2>
            <p className="mt-4 text-[#4a4a4a] text-base max-w-md mx-auto">
              A 60-minute assessment is the first step of every Carlton protocol.
            </p>
            <button onClick={() => openBook("assessment")} data-testid="program-cta-book-button" className="btn-gold mt-8">
              Book Your Assessment <ArrowRight size={15} strokeWidth={2} />
            </button>
          </FadeIn>
          <div className="mt-14 pt-8 border-t border-[#c9a14a]/25 flex flex-col sm:flex-row justify-between gap-5">
            <Link to={`/programs/${prev.slug}`} data-testid="program-prev-link" className="group flex items-center gap-3 text-left text-[#141414] hover:text-[#a8813a] transition-colors">
              <ArrowLeft size={16} className="text-[#c9a14a] group-hover:-translate-x-1 transition-transform" />
              <span>
                <span className="block text-[10px] font-bold tracking-[0.2em] uppercase text-[#4a4a4a]/70">Previous</span>
                <span className="text-sm font-semibold">{prev.title}</span>
              </span>
            </Link>
            <Link to={`/programs/${next.slug}`} data-testid="program-next-link" className="group flex items-center gap-3 text-right justify-end text-[#141414] hover:text-[#a8813a] transition-colors">
              <span>
                <span className="block text-[10px] font-bold tracking-[0.2em] uppercase text-[#4a4a4a]/70">Next</span>
                <span className="text-sm font-semibold">{next.title}</span>
              </span>
              <ArrowRight size={16} className="text-[#c9a14a] group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
